import { CommonModule } from '@angular/common';
import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { LanguageService } from '../../config/language.service';
import { AuthService } from 'src/app/features/auth/services/auth.service';
import { AppState } from 'src/app/core/app.state';
import { ICurrency, IUser } from 'src/app/features/auth/models/auth-response';
import { ICartItem } from 'src/app/features/catalogs/models/catalogs.models';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { IProduct, IVariant } from 'src/app/shared/models/products.models';
import { SharedModule } from 'src/app/shared/shared.module';
import { BASE_URL } from 'src/environments/environment';
import { CommonService } from 'src/app/shared/common.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-nav-header',
  standalone: true,
  imports: [RouterModule, CommonModule, SharedModule],
  templateUrl: './nav-header.component.html',
  styleUrls: ['./nav-header.component.scss'],
})
export class NavHeaderComponent implements OnInit {
  BASE_URL = BASE_URL;

  currentUser!: IUser;

  cartItems$!: Observable<ICartItem[]>;

  cartTotalItems = 0;
  cartTotalPrice = 0;

  cartsTotalPricePerCurrency: { total: number; currency: ICurrency | undefined; }[] = [];

  activeRoute = '';

  destroy$ = new Subject<void>();

  // Mobile menu properties
  isMobileMenuOpen = false;
  isProfileDialogOpen = false;
  profileData = {
    name: '',
    email: '',
    language: 'en',
  };

  userProfile = {
    profileImage: '',
    name: '',
    email: '',
    language: 'en',
  };

  updateData = {
    name: '',
    email: '',
    image: '',
    language: 'en',
  };

  imagePreview = '';

  showProfileDialog = false;
  private readonly MOBILE_BREAKPOINT = 768;
  currency: ICurrency | null = null;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private languageService: LanguageService,
    private authservice: AuthService,
    private commonService: CommonService,
    private dialog: MatDialog
  ) {
    this.cartItems$ = this.store.select((state) => state.catalogs.cart);
  }

  ngOnInit(): void {
    this.currentUser = LocalStorageService.get('currentUser');

    if (this.currentUser && this.currentUser.contact) {
      this.userProfile.name =
        this.currentUser.contact.contact_fname +
        ' ' +
        (this.currentUser.contact.contact_lname || '');
      this.userProfile.email = this.currentUser.contact.email || '';
      this.userProfile.profileImage = this.currentUser.contact.image_1920 || '';
      this.userProfile.language = this.getLanguage(this.currentUser.contact.lang) || 'en';
      this.currency = this.currentUser.location.currency || null;
    }

    // Check initial breakpoint and close mobile menu if on larger screen
    this.checkBreakpointAndCloseMenu();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.activeRoute = event.url;
      }
    });

    const currentNavigation = this.router.getCurrentNavigation();
    if (!currentNavigation) {
      this.activeRoute = this.router.url;
    }

    this.cartItems$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.cartTotalItems = items.map((item) => item.products && item.products.length ? item.products : []).flat().length;
      this.cartTotalPrice = items
        .map((item) => item.products && item.products.length ? item.products : [])
        .flat()
        .reduce((acc, item) => {
          // qte_selected est déjà en unités totales (total units)
          const totalUnits = item.qte_selected || 0;
          const finalPrice = this.getFinalPrice(item);
          return acc + (finalPrice * totalUnits);
        }, 0);

      this.cartsTotalPricePerCurrency = items.map((item) => {
        let total = item.products && item.products.length 
          ? item.products.reduce((acc, prod) => {
              // qte_selected est déjà en unités totales (total units)
              const totalUnits = prod.qte_selected || 0;
              const finalPrice = this.getFinalPrice(prod);
              return acc + (finalPrice * totalUnits);
            }, 0) 
          : 0;

        if(item.best_deal && item.best_deal.length) {
          // Apply formula: total * ( (1-rule1%) * (1-rule2%) * (1-ruleN%) )
          const discountMultiplier = item.best_deal.reduce((multiplier, deal) => {
            const discountPercent = Number(deal.extra_discount) || 0;
            return multiplier * (1 - discountPercent / 100);
          }, 1);

          total = Number((total * discountMultiplier).toFixed(2));
        }

        return {
          total,
          currency: item.currency
        };
      });
    });
  }

  // Listen to window resize events to auto-close mobile menu on breakpoint change
  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    if (window.innerWidth > this.MOBILE_BREAKPOINT && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  // Check current breakpoint and close mobile menu if on larger screen
  private checkBreakpointAndCloseMenu(): void {
    if (window.innerWidth > this.MOBILE_BREAKPOINT && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  getFinalPrice(item: IProduct | IVariant): number {
    if (item.best_deal && item.best_deal.length) {
      return this.getFinalPriceAfterDeal(item);
    }

    return item.final_price ? item.final_price : item.sales_price * (1 - item.discount! / 100);
  }

  getFinalPriceAfterDeal(product: IProduct | IVariant): number {
    if(!product.best_deal || !product.best_deal.length) return 0;
    
    const catalogPrice = product.final_price
      ? product.final_price
      : product.sales_price * (1 - product.discount! / 100);

    // Apply formula: catalog.price * ( (1-rule1%) * (1-rule2%) * (1-ruleN%) )
    const discountMultiplier = product.best_deal.reduce((multiplier, deal) => {
      const discountPercent = Number(deal.extra_discount) || 0;
      return multiplier * (1 - discountPercent / 100);
    }, 1);

    return Number((catalogPrice * discountMultiplier).toFixed(2));
  }

  getLanguage(language: string): string {
    if (!language || typeof language !== 'string' || language.length < 2) {
      return 'en';
    }
    // Take first two characters of the language code and convert to lowercase
    return language.substring(0, 2).toLowerCase();
  }

  // Mobile menu methods
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    this.toggleBodyScroll();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    this.toggleBodyScroll();
  }

  private toggleBodyScroll(): void {
    if (this.isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  goToCart() {
    this.router.navigate(['/cart']);
  }

  logout() {
    localStorage.removeItem('currentUser');
    this.router.navigate(['']);
  }

  ngOnDestroy() {
    // Restore body scroll if mobile menu was open
    if (this.isMobileMenuOpen) {
      document.body.style.overflow = '';
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  openProfileDialog(): void {
    this.showProfileDialog = true;
    this.profileData = {
      name:
        this.currentUser?.contact?.contact_fname + ' ' + this.currentUser?.contact?.contact_lname ||
        '',
      email: this.currentUser?.contact?.email || '',
      language: this.currentUser?.contact?.lang, // Set default or get from user preferences
      // profileImage: this.currentUser?.contact?.image_1920 || ''
    };
  }

  updateProfile(): void {
    // Get values directly from userProfile form
    const name = this.userProfile.name;
    const email = this.userProfile.email;
    const language = this.userProfile.language;
    const image = this.updateData.image || '';

    // Validation
    if (!name || name.trim() === '') {
      alert('Name cannot be empty');
      return;
    }

    console.log('Updating profile with data:', {
      name,
      email,
      language,
      imageLength: image ? image.length : 0,
      hasImage: !!image,
    });

    // Send data to API
    this.sendData(language, name, email, image);
  }

  removeProfileImage(): void {
    this.userProfile.profileImage = '';
    this.updateData.image = '';
    this.imagePreview = '';

    // Reset the file input
    const fileInput = document.getElementById('profileImage') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (e.g., max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert('File size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64String = e.target.result;

        // Update both the display image and the data to be sent
        this.userProfile.profileImage = base64String;
        this.updateData.image = base64String;
        this.imagePreview = reader.result as string;
      };

      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        this.commonService.showToast('Error reading the selected file', 'ERROR');
      };

      reader.readAsDataURL(file);
    }
  }

  closeDialog(): void {
    this.showProfileDialog = false;
    this.imagePreview = '';
  }

  langToOdooLang(lang: string): string {
    const mapping: { [key: string]: string } = {
      en: 'en_US',
      fr: 'fr_FR',
      it: 'it_IT',
    };

    return mapping[lang] || 'en_US'; // Default to 'en_US' if not found
  }

  sendData(lang: string, name: string, email: string, image: string): void {
    this.languageService.changeLanguage(lang);
    const odooLang = this.langToOdooLang(lang);
    image = image ? image.split(',')[1] : '';

    this.authservice.UpdateContact(name, image, odooLang).subscribe({
      next: (response) => {
        // Update currentUser object
        if (this.currentUser && this.currentUser.contact) {
          if (name.includes(' ')) {
            const nameParts = name.split(' ');
            this.currentUser.contact.contact_fname = nameParts[0];
            this.currentUser.contact.contact_lname = nameParts.slice(1).join(' ');
          } else {
            this.currentUser.contact.contact_fname = name;
            this.currentUser.contact.contact_lname = '';
          }
          this.currentUser.contact.email = email;
          this.currentUser.contact.lang = odooLang;
          if (image) {
            this.currentUser.contact.image_1920 = `data:image/jpeg;base64,${image}`;
          }
        }

        // Update localStorage with new data
        LocalStorageService.put('currentUser', this.currentUser);

        // Update component's userProfile object for immediate UI update
        this.userProfile = {
          ...this.userProfile,
          name: name,
          email: email,
          language: lang,
          profileImage: image ? `data:image/jpeg;base64,${image}` : this.userProfile.profileImage,
        };

        // Clear the updateData.image since it's now saved
        this.updateData.image = '';

        // Dispatch to store if needed
        this.store.dispatch({
          type: '[User] Update Profile',
          payload: {
            name,
            email,
            language: lang,
            profileImage: this.userProfile.profileImage,
          },
        });

        // Set language in service
        this.languageService.setUserLanguage(lang);

        console.log('Local data updated successfully');

        // Close dialog
        this.closeDialog();
      },
      error: (error) => {
        console.error('Error updating profile:', error);
        alert('There was an error updating your profile. Please try again later.');
      },
    });
  }
}
